import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { api, ApiError } from './api';
import { currentSchool, openSchool } from './school';
import type { Me, TenantRole } from './types';

const ROLE_RANK: Record<TenantRole, number> = { REQUESTER: 0, VIEWER: 1, TECHNICIAN: 2, ADMIN: 3 };

/** True when `role` is at least `minimum`. */
export const hasRole = (role: TenantRole | null | undefined, minimum: TenantRole) => !!role && ROLE_RANK[role] >= ROLE_RANK[minimum];

interface AuthValue {
  me: Me | null;
  loading: boolean;
  /** Password sign-in. Returns the school the session opened, if any. */
  login(email: string, password: string): Promise<{ school: string | null }>;
  logout(): Promise<void>;
  /** Opens another school at /{slug}. */
  switchSchool(slug: string): void;
  /** True when the user's role in the current school is at least `role`. */
  can(role: TenantRole): boolean;
  /** True when the user's facilities role is at least `role`. Always false when the school doesn't have Facilities. */
  canFacilities(role: TenantRole): boolean;
}

const AuthContext = createContext<AuthValue | null>(null);

/** What the sign-in pages can offer: resetting a forgotten password needs Edvora's own email. */
export const useAuthOptions = () =>
  useQuery({ queryKey: ['auth', 'options'], queryFn: () => api.get<{ passwordReset: boolean }>('/auth/options'), staleTime: 5 * 60_000 });

async function fetchMe() {
  try {
    return await api.get<Me>('/auth/me');
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return null;
    throw err;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const meQuery = useQuery({ queryKey: ['me'], queryFn: fetchMe, staleTime: 5 * 60_000, retry: false });

  useEffect(() => {
    const onUnauthorized = () => queryClient.setQueryData(['me'], null);
    const onPlanChanged = () => void queryClient.invalidateQueries({ queryKey: ['me'] });
    window.addEventListener('ec:unauthorized', onUnauthorized);
    window.addEventListener('ec:plan-changed', onPlanChanged);
    return () => {
      window.removeEventListener('ec:unauthorized', onUnauthorized);
      window.removeEventListener('ec:plan-changed', onPlanChanged);
    };
  }, [queryClient]);

  const login = useCallback(
    async (email: string, password: string) => {
      const result = await api.post<{ school: string | null }>('/auth/login', { email, password, school: currentSchool ?? undefined });
      await queryClient.resetQueries();
      return { school: result.school };
    },
    [queryClient],
  );

  const logout = useCallback(async () => {
    await api.post('/auth/logout');
    queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== 'me' && query.queryKey[0] !== 'public-school' });
    queryClient.setQueryData(['me'], null);
  }, [queryClient]);

  const switchSchool = useCallback((slug: string) => {
    if (slug !== currentSchool) openSchool(slug);
  }, []);

  const me = meQuery.data ?? null;
  const value = useMemo<AuthValue>(
    () => ({
      me,
      loading: meQuery.isLoading,
      login,
      logout,
      switchSchool,
      can: (role) => hasRole(me?.role, role),
      canFacilities: (role) => hasRole(me?.facilitiesRole, role),
    }),
    [me, meQuery.isLoading, login, logout, switchSchool],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider');
  return value;
}
